import { useMemo, useRef } from 'react';

import { cva } from 'class-variance-authority';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { CalendarPlusIcon, MapPinPlusIcon } from 'lucide-react';
import { useDispatch } from 'react-redux';

import {
  SchedulerCalendarEvent,
  SchedulerCalendarTrip,
  SchedulerReferenceUser,
  SchedulerUserSetting,
  useSchedulerCalendarData,
} from '@/app/services/serviceScheduler';
import { openForCreate as openCreateEventModal } from '@/app/slices/createEventModalSlice';
import { openForEdit as openEditEventModal } from '@/app/slices/createEventModalSlice';
import { openForCreate as openCreateTripModal } from '@/app/slices/createTripModalSlice';
import { openForEdit as openEditTripModal } from '@/app/slices/createTripModalSlice';
import { cn } from '@/lib/utils';
import { chunk } from '@/utils';

import { Menu, type MenuHandle, type MenuItem } from '../compat-controls';
import { DateBox, type DateBoxView } from '../date-box';
import { EventChip } from '../event-chip';

/**
 * Generates an array of dates for a week grid view of a given week.
 * Includes days from the previous and next months to complete the week.
 */
function getWeekDays(date: Date) {
  const startOfCurrentWeek = startOfWeek(date);
  const endOfCurrentWeek = endOfWeek(date);
  return eachDayOfInterval({
    start: startOfCurrentWeek,
    end: endOfCurrentWeek,
  });
}

/**
 * Generates an array of dates for a calendar grid view of a given month.
 * Includes days from the previous and next months to complete the weeks.
 */
function getMonthDays(date: Date) {
  const startOfCurrentMonth = startOfMonth(date);
  const startOfCalendar = startOfWeek(startOfCurrentMonth);

  const endOfCurrentMonth = endOfMonth(date);
  const endOfCalendar = endOfWeek(endOfCurrentMonth);

  return eachDayOfInterval({
    start: startOfCalendar,
    end: endOfCalendar,
  });
}

function generateCalendarDays({
  view,
  activeDateFormatted,
  activeDate,
}: {
  view: DateBoxView;
  activeDateFormatted: string;
  activeDate: Date;
}) {
  if (view === 'week') {
    const days = getWeekDays(activeDate).map((date) => {
      const formattedDate = format(date, 'yyyy-MM-dd');
      return {
        date,
        formattedDate,
        isActive: activeDateFormatted === formattedDate,
      };
    });

    return {
      view: 'week' as const,
      days: days.map((date, dayOfWeek, currentWeek) => {
        const isRightOfActive = !!currentWeek[dayOfWeek - 1]?.isActive;
        const isLeftOfActive = !!currentWeek[dayOfWeek + 1]?.isActive;
        return {
          ...date,
          isRightOfActive,
          isLeftOfActive,
        };
      }),
    };
  }

  if (view === 'month') {
    const days = getMonthDays(activeDate).map((date) => {
      const formattedDate = format(date, 'yyyy-MM-dd');
      return {
        date,
        formattedDate,
        isActive: activeDateFormatted === formattedDate,
      };
    });

    const DAYS_IN_WEEK = 7;

    return {
      view: 'month' as const,
      weeks: chunk(days, DAYS_IN_WEEK).map((currentWeek, weekIndex, weeks) =>
        currentWeek.map((date, dayOfWeek) => {
          const isBelowActive = !!weeks[weekIndex - 1]?.[dayOfWeek]?.isActive;
          const isAboveActive = !!weeks[weekIndex + 1]?.[dayOfWeek]?.isActive;
          const isRightOfActive = !!currentWeek[dayOfWeek - 1]?.isActive;
          const isLeftOfActive = !!currentWeek[dayOfWeek + 1]?.isActive;

          return {
            ...date,
            isAboveActive,
            isBelowActive,
            isRightOfActive,
            isLeftOfActive,
          };
        }),
      ),
    };
  }

  return { view: 'day' as const };
}

const dateBox = cva('', {
  variants: {
    view: {
      day: 'h-full border-l-0',
      week: 'w-1/7 border-t-2 border-b-2',
      month: 'h-full w-1/7',
    },
    isActive: { true: 'border-sky-400' },
    isAboveActive: { true: 'border-b-sky-400' },
    isBelowActive: { true: 'border-t-sky-400' },
    isLeftOfActive: { true: 'border-r-sky-400' },
    isRightOfActive: { true: 'border-l-sky-400' },
    isTopRow: { true: 'border-t-2' },
    isLastRow: { true: 'border-b-2' },
    isFirstColumn: { true: 'border-l-2' },
    isLastColumn: { true: 'border-r-2' },
  },
});

type ScheduledItem = {
  id: string;
  label: string;
  kind: 'trip' | 'event';
  colorHex?: string;
  source:
    | { type: 'trip'; trip: SchedulerCalendarTrip }
    | { type: 'event'; event: SchedulerCalendarEvent };
};

export function MainCalendar(props: {
  view: DateBoxView;
  setView: React.Dispatch<React.SetStateAction<DateBoxView>>;
  activeDate: Date;
  setActiveDate: React.Dispatch<React.SetStateAction<Date>>;
  selectedRegions?: string[];
  selectedSiteCodes?: string[];
  activeUsers?: Record<string, boolean>;
  userSettings?: SchedulerUserSetting[];
  referenceUsers?: SchedulerReferenceUser[];
}) {
  const dispatch = useDispatch();

  const userColorMap = useMemo(() => {
    const settingsById = new Map(
      (props.userSettings ?? []).map((s) => [s.userId.toLowerCase(), s]),
    );
    const colorMap = new Map<string, string>();
    for (const user of props.referenceUsers ?? []) {
      const setting =
        settingsById.get(user.equipmentId.toLowerCase()) ??
        (user.systemUserId
          ? settingsById.get(user.systemUserId.toLowerCase())
          : undefined);
      if (setting?.colorHex) {
        colorMap.set(user.equipmentId.toLowerCase(), setting.colorHex);
        if (user.systemUserId) {
          colorMap.set(user.systemUserId.toLowerCase(), setting.colorHex);
        }
      }
    }
    return colorMap;
  }, [props.userSettings, props.referenceUsers]);

  const visibleUserIds = useMemo(() => {
    const active = props.activeUsers;
    if (!active) return null;
    const normalizedActiveIds = new Set(
      Object.entries(active)
        .filter(([, isActive]) => !!isActive)
        .map(([id]) => id.trim().toLowerCase()),
    );
    const ids = new Set<string>();
    for (const user of props.referenceUsers ?? []) {
      const equipmentId = user.equipmentId.trim().toLowerCase();
      const systemUserId = user.systemUserId?.trim().toLowerCase();
      if (
        normalizedActiveIds.has(equipmentId) ||
        (systemUserId && normalizedActiveIds.has(systemUserId))
      ) {
        ids.add(equipmentId);
        if (systemUserId) ids.add(systemUserId);
      }
    }
    for (const id of normalizedActiveIds) ids.add(id);
    return ids;
  }, [props.activeUsers, props.referenceUsers]);

  const knownUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const user of props.referenceUsers ?? []) {
      ids.add(user.equipmentId.trim().toLowerCase());
      if (user.systemUserId) ids.add(user.systemUserId.trim().toLowerCase());
    }
    return ids;
  }, [props.referenceUsers]);

  const activeDateFormatted = format(props.activeDate, 'yyyy-MM-dd');
  const rangeStart = startOfWeek(startOfMonth(props.activeDate));
  const rangeEnd = endOfWeek(endOfMonth(props.activeDate));
  const selectedRegion = props.selectedRegions?.[0];
  const selectedSiteCode = props.selectedSiteCodes?.[0];
  const { trips, events, isLoading, error } = useSchedulerCalendarData({
    startUtc: rangeStart.toISOString(),
    endUtc: rangeEnd.toISOString(),
    ...(selectedRegion ? { region: selectedRegion } : {}),
    ...(selectedSiteCode ? { siteCode: selectedSiteCode } : {}),
    startRow: 1,
    endRow: 1000,
  });

  const scheduledItemsByDay = useMemo(() => {
    const map = new Map<string, ScheduledItem[]>();
    const addForDay = (date: Date, item: ScheduledItem) => {
      const key = format(date, 'yyyy-MM-dd');
      const current = map.get(key) ?? [];
      current.push(item);
      map.set(key, current);
    };

    const getTripUserIds = (trip: SchedulerCalendarTrip) =>
      [trip.psEmbryologistId, trip.psInternId]
        .filter(Boolean)
        .map((id) => id!.trim().toLowerCase());

    const getEventUserIds = (event: SchedulerCalendarEvent) =>
      [event.psVetId, event.psEmbryologistId, ...(event.attendees ?? [])]
        .filter(Boolean)
        .map((id) => id!.trim().toLowerCase());

    const resolveColor = (userIds: string[]) => {
      for (const uid of userIds) {
        const color = userColorMap.get(uid);
        if (color) return color;
      }
      return undefined;
    };

    const isVisible = (userIds: string[]) => {
      if (!visibleUserIds) return true; // no filter state yet
      if (userIds.length === 0) return true; // no assigned tech — always show
      const knownAssignedUserIds = userIds.filter((uid) =>
        knownUserIds.has(uid),
      );
      if (knownAssignedUserIds.length === 0) return true;
      return knownAssignedUserIds.every((uid) => visibleUserIds.has(uid));
    };

    trips.forEach((trip) => {
      if (!trip.startUtc) return;
      const tripUserIds = getTripUserIds(trip);
      if (!isVisible(tripUserIds)) return;
      const start = new Date(trip.startUtc);
      const end = trip.endUtc ? new Date(trip.endUtc) : start;
      const colorHex = resolveColor(tripUserIds);
      eachDayOfInterval({ start, end }).forEach((day) => {
        if (isWithinInterval(day, { start: rangeStart, end: rangeEnd })) {
          addForDay(day, {
            id: `trip-${trip.tripId}`,
            label: trip.shipmentNumber || 'Trip',
            kind: 'trip',
            colorHex,
            source: { type: 'trip', trip },
          });
        }
      });
    });

    events.forEach((event) => {
      if (!event.startUtc) return;
      const eventUserIds = getEventUserIds(event);
      if (!isVisible(eventUserIds)) return;
      const start = new Date(event.startUtc);
      const end = event.endUtc ? new Date(event.endUtc) : start;
      const colorHex = resolveColor(eventUserIds);
      eachDayOfInterval({ start, end }).forEach((day) => {
        if (isWithinInterval(day, { start: rangeStart, end: rangeEnd })) {
          addForDay(day, {
            id: `event-${event.eventId}`,
            label: event.description || event.type || 'Event',
            kind: 'event',
            colorHex,
            source: { type: 'event', event },
          });
        }
      });
    });

    return map;
  }, [
    events,
    knownUserIds,
    rangeEnd,
    rangeStart,
    trips,
    userColorMap,
    visibleUserIds,
  ]);

  const menu = useRef<MenuHandle>(null);
  const menuItems = useMemo<MenuItem[]>(
    () => [
      {
        label: 'Add',
        items: [
          {
            label: 'Trip',
            icon: <MapPinPlusIcon />,
            command() {
              dispatch(openCreateTripModal());
            },
          },
          {
            label: 'Event',
            icon: <CalendarPlusIcon />,
            command() {
              dispatch(openCreateEventModal());
            },
          },
        ],
      },
    ],
    [dispatch],
  );

  function showMenu(e: React.MouseEvent) {
    menu.current?.show(e);
  }

  const handleScheduledItemClick = (item: ScheduledItem, date: Date) => {
    props.setActiveDate(date);
    if (item.source.type === 'trip') {
      const trip = item.source.trip;
      dispatch(
        openEditTripModal({
          id: trip.tripId,
          recurring: trip.recurring,
          frequency:
            (trip.frequency as
              | 'annual'
              | 'monthly'
              | 'bi-monthly'
              | 'weekly') || 'monthly',
          shipmentNumber: trip.shipmentNumber || '',
          psEmbryologist: trip.psEmbryologistId ?? null,
          psIntern: trip.psInternId ?? null,
          stops: [
            {
              id: `${trip.tripId}-stop`,
              region: null,
              site: null,
              startDate: trip.startUtc ?? null,
              endDate: trip.endUtc ?? trip.startUtc ?? null,
              psVet: null,
              accountNumber: '',
              description: '',
              comments: '',
              enteredBy: '',
              status: 'tentative',
              opu: null,
              ftTx: null,
              fzTx: null,
              dfrs: null,
              pregs: null,
              flush: null,
            },
          ],
        }),
      );
      return;
    }

    const event = item.source.event;
    dispatch(
      openEditEventModal({
        id: event.eventId,
        type: event.type ?? null,
        psVet: event.psVetId ?? null,
        startDate: event.startUtc ?? null,
        endDate: event.endUtc ?? null,
        coveringSite: event.coveringSiteCode ?? '',
        description: event.description ?? '',
        enteredBy: event.enteredBy ?? '',
        psEmbryologist: event.psEmbryologistId ?? null,
        attendees: event.attendees ?? [],
      }),
    );
  };

  const days = generateCalendarDays({
    view: props.view,
    activeDate: props.activeDate,
    activeDateFormatted,
  });

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load calendar data. Please try again.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm text-gray-500">Loading calendar...</div>
      </div>
    );
  }

  return (
    <>
      <Menu model={menuItems} popup ref={menu} popupAlignment="right" />

      {days.view === 'month' ? (
        <div className="flex h-full flex-col px-2">
          {days.weeks.map((days, weekIndex, weeks) => (
            <div key={weekIndex} className="flex min-h-0 flex-1 flex-row">
              {days.map((day, dayOfWeek) => (
                <DateBox
                  key={day.formattedDate}
                  date={day.date}
                  view="month"
                  className={cn(
                    dateBox({
                      view: props.view,
                      isActive: day.isActive,
                      isAboveActive: day.isAboveActive,
                      isBelowActive: day.isBelowActive,
                      isLeftOfActive: day.isLeftOfActive,
                      isRightOfActive: day.isRightOfActive,
                      isTopRow: weekIndex === 0,
                      isLastRow: weekIndex === weeks.length - 1,
                      isFirstColumn: dayOfWeek === 0,
                      isLastColumn: dayOfWeek === days.length - 1,
                    }),
                  )}
                  onAdd={showMenu}
                  onClick={() => props.setActiveDate(day.date)}
                  onDoubleClick={() => {
                    props.setActiveDate(day.date);
                    props.setView('week');
                  }}
                  disabled={props.activeDate.getMonth() !== day.date.getMonth()}
                >
                  {(scheduledItemsByDay.get(day.formattedDate) ?? []).map(
                    (item) => (
                      <EventChip
                        key={item.id}
                        view="month"
                        onClick={() => {
                          handleScheduledItemClick(item, day.date);
                        }}
                        colorHex={item.colorHex}
                      >
                        {item.label}
                      </EventChip>
                    ),
                  )}
                </DateBox>
              ))}
            </div>
          ))}
        </div>
      ) : days.view === 'week' ? (
        <div className="flex h-full flex-row px-2">
          {days.days.map((day, dayOfWeek, days) => (
            <DateBox
              key={day.formattedDate}
              date={day.date}
              view="week"
              className={cn(
                dateBox({
                  view: props.view,
                  isActive: day.isActive,
                  isLeftOfActive: day.isLeftOfActive,
                  isRightOfActive: day.isRightOfActive,
                  isFirstColumn: dayOfWeek === 0,
                  isLastColumn: dayOfWeek === days.length - 1,
                }),
              )}
              onAdd={showMenu}
              onClick={() => props.setActiveDate(day.date)}
              onDoubleClick={() => props.setActiveDate(day.date)}
              disabled={props.activeDate.getMonth() !== day.date.getMonth()}
            >
              {(scheduledItemsByDay.get(day.formattedDate) ?? []).map(
                (item) => (
                  <EventChip
                    key={item.id}
                    view="week"
                    onClick={() => {
                      handleScheduledItemClick(item, day.date);
                    }}
                    colorHex={item.colorHex}
                  >
                    {item.label}
                  </EventChip>
                ),
              )}
            </DateBox>
          ))}
        </div>
      ) : (
        <DateBox
          date={props.activeDate}
          view="day"
          className={cn(dateBox({ view: props.view }))}
          onAdd={showMenu}
        >
          {(scheduledItemsByDay.get(activeDateFormatted) ?? []).map((item) => (
            <EventChip
              key={item.id}
              view="day"
              onClick={() => {
                handleScheduledItemClick(item, props.activeDate);
              }}
              colorHex={item.colorHex}
            >
              {item.label}
            </EventChip>
          ))}
        </DateBox>
      )}
    </>
  );
}
